/** localStorage keys — MFA gate + optional binding to MSAL account (account switch safety). */
export const MFA_VALIDATE_KEY = "mfa-validate";
export const MFA_VALIDATE_HOME_ID_KEY = "mfa-validate-home-id";

/**
 * sessionStorage — last successful SendEmailMfa / SendSmsMfa payload so
 * VerifyMfaOtp still has UserMfaHistoryId if /authenticate mounts without
 * router state (App Redirect race) or remounts before React state hydrates.
 */
export const MFA_PENDING_HISTORY_KEY = "mxhh_mfa_pending_history";

/** Dedupes auto SendEmailMfa between useMfa preflight and MFA page init. */
export const MFA_INIT_SEND_TS_KEY = "mxhh_mfa_init_send_ts";

/** Pending OTP send payload TTL (15 minutes). */
const MFA_PENDING_HISTORY_TTL_MS = 15 * 60 * 1000;

/**
 * Fired on the window after `mfa-validate` is written or cleared.
 * `containers/routes.js` lives above `AppContextProvider`, so it does not re-render
 * when MFA state updates in React — this keeps `AllRoutes` / axios response
 * interceptor in sync without a full page reload.
 */
export const MFA_STORAGE_CHANGED_EVENT = "mxhh-mfa-storage-changed";

function dispatchMfaStorageChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MFA_STORAGE_CHANGED_EVENT));
}

/**
 * Normalize UserMfaHistoryId from a Send*Mfa `data` object.
 * @param {Record<string, unknown> | null | undefined} data
 * @returns {number | null}
 */
export function resolveMfaHistoryId(data) {
  if (!data || typeof data !== "object") {
    return null;
  }
  const raw =
    data.id ?? data.Id ?? data.userMfaHistoryId ?? data.UserMfaHistoryId;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Persist the last OTP-send response so Verify can recover UserMfaHistoryId.
 * @param {Record<string, unknown> | null | undefined} data
 * @returns {boolean}
 */
export function persistPendingMfaSendPayload(data) {
  if (typeof window === "undefined") return false;
  const id = resolveMfaHistoryId(data);
  if (id == null) {
    return false;
  }
  try {
    sessionStorage.setItem(
      MFA_PENDING_HISTORY_KEY,
      JSON.stringify({
        data: { ...data, id },
        savedAt: Date.now(),
      }),
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * @returns {Record<string, unknown> | null}
 */
export function readPendingMfaSendPayload() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(MFA_PENDING_HISTORY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed?.savedAt &&
      Date.now() - Number(parsed.savedAt) > MFA_PENDING_HISTORY_TTL_MS
    ) {
      clearPendingMfaSendPayload();
      return null;
    }
    if (resolveMfaHistoryId(parsed?.data) == null) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function clearPendingMfaSendPayload() {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(MFA_PENDING_HISTORY_KEY);
}

/** Mark that SendEmailMfa already ran (avoids duplicate OTP within a few seconds). */
export function markMfaInitSendTimestamp() {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(MFA_INIT_SEND_TS_KEY, String(Date.now()));
}

/**
 * Mark MFA as satisfied for this browser session / remember-me window.
 * @param {{ homeAccountId?: string } | null | undefined} account
 */
export function persistMfaValidatedForAccount(account) {
  localStorage.setItem(MFA_VALIDATE_KEY, "true");
  if (account?.homeAccountId) {
    localStorage.setItem(MFA_VALIDATE_HOME_ID_KEY, account.homeAccountId);
  }
  clearPendingMfaSendPayload();
  dispatchMfaStorageChanged();
}

/**
 * Bind MFA flag to the current MSAL account without re-firing storage events.
 * Used when `mfa-validate` is already set but homeAccountId was missing at verify time.
 * Re-dispatching MFA_STORAGE_CHANGED would remount authenticated routes and flash the UI.
 * @param {{ homeAccountId?: string } | null | undefined} account
 */
export function bindMfaValidatedHomeAccountId(account) {
  if (!account?.homeAccountId || !readMfaValidatedRaw()) {
    return;
  }
  if (getStoredMfaHomeAccountId() === account.homeAccountId) {
    return;
  }
  localStorage.setItem(MFA_VALIDATE_HOME_ID_KEY, account.homeAccountId);
}

export function clearMfaValidated() {
  localStorage.removeItem(MFA_VALIDATE_KEY);
  localStorage.removeItem(MFA_VALIDATE_HOME_ID_KEY);
  clearPendingMfaSendPayload();
  dispatchMfaStorageChanged();
}

export function readMfaValidatedRaw() {
  return Boolean(localStorage.getItem(MFA_VALIDATE_KEY));
}

/** @returns {string | null} */
export function getStoredMfaHomeAccountId() {
  return localStorage.getItem(MFA_VALIDATE_HOME_ID_KEY);
}
